import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { resolveImg } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

/**
 * Generic CRUD manager.
 * props: title, endpoint, fields[], columns[], prefix, defaults
 * field: { name, label, type: text|textarea|number|date|select|image, options?, placeholder?, full? }
 */
const CrudManager = ({ title, description, endpoint, fields, columns, prefix, defaults = {} }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(endpoint).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [endpoint]);

  const openAdd = () => { setEditing(null); setForm(defaults); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...defaults, ...item }); setOpen(true); };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} wajib diisi`); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, form);
        toast.success("Data berhasil diperbarui");
      } else {
        await api.post(endpoint, form);
        toast.success("Data berhasil ditambahkan");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan data");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleteId}`);
      toast.success("Data berhasil dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus data");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        <Button onClick={openAdd} className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid={`${prefix}-add-button`}>
          <Plus className="h-4 w-4" /> Tambah
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400" data-testid={`${prefix}-empty`}>
          <Inbox className="mx-auto h-10 w-10 opacity-40" />
          <p className="mt-3">Belum ada data. Klik "Tambah" untuk menambahkan.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4 px-5 py-4" data-testid={`${prefix}-row-${item.id}`}>
                {columns.filter((c) => c.image).map((c) => (
                  <img key={c.name} src={resolveImg(item[c.name])} alt="" className="h-14 w-14 shrink-0 rounded-lg border object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                ))}
                <div className="min-w-0 flex-1">
                  {columns.filter((c) => !c.image).map((c, i) => (
                    <p key={c.name} className={i === 0 ? "font-medium text-slate-800 truncate" : "text-sm text-slate-500 truncate"}>
                      {c.render ? c.render(item) : item[c.name]}
                    </p>
                  ))}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="icon" variant="outline" onClick={() => openEdit(item)} data-testid={`${prefix}-edit-${item.id}`} className="h-9 w-9 border-slate-200 text-slate-600 hover:text-emerald-700">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => setDeleteId(item.id)} data-testid={`${prefix}-delete-${item.id}`} className="h-9 w-9 border-red-100 text-red-500 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={`${prefix}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Tambah"} {title}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full || f.type === "image" || f.type === "textarea" ? "sm:col-span-2" : ""}>
                {f.type === "image" ? (
                  <ImageUpload label={f.label} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} testid={`${prefix}-${f.name}`} />
                ) : (
                  <>
                    <label className="text-sm font-medium text-slate-700">{f.label}</label>
                    {f.type === "textarea" ? (
                      <Textarea value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder} rows={f.rows || 4} data-testid={`${prefix}-${f.name}-input`} className="mt-1.5" />
                    ) : f.type === "select" ? (
                      <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                        <SelectTrigger className="mt-1.5" data-testid={`${prefix}-${f.name}-select`}><SelectValue placeholder="Pilih..." /></SelectTrigger>
                        <SelectContent>
                          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })} placeholder={f.placeholder} data-testid={`${prefix}-${f.name}-input`} className="mt-1.5" />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid={`${prefix}-save-button`}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid={`${prefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus data ini?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan. Data akan dihapus secara permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid={`${prefix}-confirm-delete`}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CrudManager;
